// Draft-07 subset only: string / number / integer / boolean / array<scalar> / object.
// anyOf/oneOf/allOf intentionally unsupported — checkpoint forms in production
// stay flat per UX cap; deeper composition triggers A6 upgrade path.
export interface Draft07Schema {
  type?: string | string[];
  properties?: Record<string, Draft07Schema>;
  items?: Draft07Schema;
  enum?: unknown[];
  title?: string;
  description?: string;
  required?: string[];
  format?: string;
  minimum?: number;
  maximum?: number;
  minLength?: number;
  maxLength?: number;
  default?: unknown;
}

export type ValidationError = { path: string; message: string };

const SUPPORTED_TYPES = new Set([
  "string", "number", "integer", "boolean", "array", "object",
]);

function validateNode(
  schema: unknown,
  path: string,
  errors: ValidationError[],
): void {
  if (typeof schema !== "object" || schema === null || Array.isArray(schema)) {
    errors.push({ path, message: "Schema node must be an object" });
    return;
  }

  const s = schema as Record<string, unknown>;

  // type field validation
  if (s.type !== undefined) {
    const types = Array.isArray(s.type) ? s.type : [s.type];
    for (const t of types) {
      if (!SUPPORTED_TYPES.has(t as string)) {
        errors.push({ path: `${path}.type`, message: `Unsupported type: "${t}"` });
      }
    }
    const primary = types[0] as string;

    if (primary === "object" && s.properties !== undefined) {
      if (typeof s.properties !== "object" || s.properties === null) {
        errors.push({ path: `${path}.properties`, message: "Must be an object" });
      } else {
        for (const [key, val] of Object.entries(s.properties as Record<string, unknown>)) {
          validateNode(val, `${path}.properties.${key}`, errors);
        }
      }
    }

    if (primary === "array" && s.items !== undefined) {
      validateNode(s.items, `${path}.items`, errors);
    }
  } else if (s.enum === undefined && s.properties === undefined) {
    // A schema node without type/enum/properties is structurally incomplete
    // for our subset — flag it unless it's a root-level $schema meta node.
    if (path !== "$") {
      errors.push({ path, message: "Schema node missing required 'type' field" });
    }
  }

  // Reject composition keywords explicitly so editors see a clear message
  for (const kw of ["anyOf", "oneOf", "allOf", "$ref"]) {
    if (s[kw] !== undefined) {
      errors.push({ path: `${path}.${kw}`, message: `"${kw}" is not supported in this form builder` });
    }
  }
}

/**
 * Validates that `raw` is parseable JSON and conforms to Draft-07 subset.
 * Returns an array of errors — empty means valid.
 */
export function validateDraft07Subset(raw: string): ValidationError[] {
  const errors: ValidationError[] = [];

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    errors.push({ path: "$", message: `Invalid JSON: ${(e as Error).message}` });
    return errors;
  }

  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    errors.push({ path: "$", message: "Root schema must be a JSON object" });
    return errors;
  }

  validateNode(parsed, "$", errors);
  return errors;
}
